import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { useFetchModelsData } from '@/modules/events/api';
import { EventPrinterStatus } from '@/modules/events/constants';

import { prepareFields, prepareModelOptions } from '../helpers/prepare';
import { useModelColumns } from './use-model-columns';
import { useModelSubColumns } from './use-model-sub-columns';

export const useModels = () => {
  const { control, watch, reset, getValues } = useFormContext();
  const [createGcrDetails, setCreateGcrDetails] = useState(null);

  const { optionsData, models, printers, gcrs, isFetching, refetchGcrs } =
    useFetchModelsData();

  const formFields = watch('models');
  const formPrinters = watch('printers', [
    {
      name: '',
      printerId: '',
      type: '',
      status: EventPrinterStatus.Enabled,
    },
  ]);

  const { remove, update, append, replace, move } = useFieldArray({
    control,
    name: 'models',
  });

  const typeOptions = optionsData?.types || [];
  const genderOptions = optionsData?.genders || [];
  const sizeOptions = optionsData?.sizes || [];
  const modelOptions = useMemo(() => prepareModelOptions(models), [models]);

  const columns = useModelColumns({
    form: {
      update,
      remove,
    },
    options: {
      modelOptions,
      sizeOptions,
    },
  });
  const subColumns = useModelSubColumns({
    form: {
      update,
    },
    onSetGcrDetails: setCreateGcrDetails,
  });

  const preparedFields = (
    prepareFields({
      form: {
        formFields,
        formPrinters,
        update,
      },
      options: {
        modelOptions,
      },
      data: {
        models,
        printers,
        gcrs,
      },
    }) || []
  ).map((field) => ({
    ...field,
    sizes: [...field.sizes].sort((a, b) => {
      const aOrder = sizeOptions.find((o) => o.id === a.sizeId)?.sortOrder ?? 0;
      const bOrder = sizeOptions.find((o) => o.id === b.sizeId)?.sortOrder ?? 0;
      return aOrder - bOrder;
    }),
  }));

  const initialized = useRef(false);

  useEffect(() => {
    if (isFetching || initialized.current) return;
    initialized.current = true;
    replace(preparedFields);
    reset(getValues(), { keepValues: true });
  }, [isFetching]);

  return {
    data: preparedFields,
    isFetching,
    columns,
    subColumns,
    options: {
      modelOptions,
      typeOptions,
      genderOptions,
      sizeOptions,
    },
    gcr: {
      createGcrDetails,
      setCreateGcrDetails,
      refetchGcrs,
    },
    form: {
      append,
      update,
      move,
    },
  };
};
